import { Autocomplete, Button, Fade, Grid, Skeleton, TextField, Typography, useTheme } from '@mui/material';
import { EditLocationOutlined } from '@mui/icons-material';
import React, { FunctionComponent, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import AnalyticsService, { IMapDataResponse } from '../../../../shared/services/analytics/analytics.service';
import { buildGenericSeriesStack, valueFormatter } from '../../../../shared/interfaces/analytics';
import { stackedRowTotals } from '../../../../shared/utils/analytics-utils';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'; import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import EmissionService from '../../../../shared/services/emission/emission.service';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import LocationService from '../../../../shared/services/location/location.service';
import { ILocation } from '../../../../shared/services/location/location.model';
import { GraphBarChartStacked } from '../graphs/graph-bar-chart-stacked';
import { GraphDonutChart } from '../graphs/graph-donut-chart';
import { DrillDownDrawer } from '../drill-down-drawer';
import { useDrillDown } from '../use-drill-down';
import { useDrillDimensionLabel } from '../use-drill-dimension-label';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { AnalyticsByLocationSkeleton } from './analytics-by-location-skeleton';
import { EmissionsTable } from '../../emissons-components/overview/emissions-table';
import { IMapPoint } from '../graphs/graph-map-chart';
import { UpdateUnmappedLocationsDialog } from '../../../dialogs/organisation/update-unmapped-locations-dialog';
import { UserDetailsContext } from '../../../../context/user-details/user-details';

const MAP_HEIGHT = 420;

// Leaflet is ~150KB and is only ever needed on this tab. The app has no route-level code
// splitting, so loading it lazily here is what keeps it out of the initial bundle.
const GraphMapChart = React.lazy(() =>
  import('../graphs/graph-map-chart').then((module) => ({ default: module.GraphMapChart })),
);

export const AnalyticsByLocation: FunctionComponent<{
  reportPeriodId: string;
  emissionType: 'marketBased' | 'locationBased';
  setLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, the breakdown donut carries the comparison period as an inner ring. */
  comparisonPeriod?: { id: string; label: string };
  periodLabel?: string;
}> = ({ reportPeriodId, emissionType, setLoaded, comparisonPeriod, periodLabel }) => {
  // Services
  const analyticsService = new AnalyticsService();
  const emissionService = new EmissionService();
  const locationService = LocationService.getInstance();
  // Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  const userDetailsContext = useContext(UserDetailsContext);
  // Matches the Locations page: an analyst without admin cannot create/edit locations.
  const roles = userDetailsContext.userDetails?.roles || [];
  const canEditLocations = !(roles.includes('analyst') && !roles.includes('admin'));
  // State
  const [pieCountryEmissions, setPieCountryEmissions] = useState<any>();
  const [comparisonPieCountryEmissions, setComparisonPieCountryEmissions] = useState<any>();
  const [stackedLocationEmissions, setStackedLocationEmissions] = useState<any>();
  const [comparisonStackedLocationEmissions, setComparisonStackedLocationEmissions] = useState<any>();
  const [mapEmissions, setMapEmissions] = useState<IMapDataResponse>();
  const [updateLocationsOpen, setUpdateLocationsOpen] = useState(false);
  const [emissionRows, setEmissionRows] = useState<IEmission[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [allLocations, setAllLocations] = useState<ILocation[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [order, setOrder] = useState<'asc' | 'desc' | undefined>();
  // Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);
  //Variables
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
  // Click-to-drill: a stale selection clears when the period, basis or comparison changes.
  const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';
  const drill = useDrillDown([requiredReportPeriodId, emissionType, comparisonPeriod?.id]);
  const dimLabel = useDrillDimensionLabel();

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      if (setLoaded) setLoaded(true);
      return;
    }

    setIsLoading(true);
    const expectedRequests = comparisonPeriod ? 6 : 4;
    let completedRequests = 0;
    const checkLoadingStatus = () => {
      completedRequests += 1;
      if (completedRequests === expectedRequests) {
        setIsLoading(false);
        if (setLoaded) setLoaded(true);
      }
    };

    if (comparisonPeriod) {
      analyticsService
        .getPieData(comparisonPeriod.id, `?data=country&topN=all`)
        .then((response) => {
          setComparisonPieCountryEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonPieCountryEmissions(undefined);
          checkLoadingStatus();
        });

      analyticsService
        .getStackedData(comparisonPeriod.id, `?data=location&topN=all`)
        .then((response) => {
          setComparisonStackedLocationEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonStackedLocationEmissions(undefined);
          checkLoadingStatus();
        });
    } else {
      setComparisonPieCountryEmissions(undefined);
      setComparisonStackedLocationEmissions(undefined);
    }

    analyticsService
      .getMapData(requiredReportPeriodId, `?data=location`)
      .then((response) => {
        setMapEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getPieData(requiredReportPeriodId, `?data=country&topN=all`)
      .then((response) => {
        setPieCountryEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getStackedData(requiredReportPeriodId, `?data=location&topN=all`)
      .then((response) => {
        setStackedLocationEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    emissionService
      .getByDomainHighest(
        requiredReportPeriodId,
        'location',
        0,
        perPage,
        orderBy,
        order,
      )
      .then((emissionsResponse) => {
        const locationId = emissionsResponse.rows[0]?.locationId ?? '';
        setSelectedLocationId(locationId);
        setEmissionRows(emissionsResponse.rows);
        setTotalRecords(emissionsResponse.count);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });
  }, [requiredReportPeriodId, comparisonPeriod?.id]);

  useEffect(() => {
    locationService
      .getAllLocations()
      .then(setAllLocations)
      .catch((err) => console.log(err));
  }, []);

  const getEmissionsForLocation = (reportPeriodId: string, locationId: string, page: number) => {
    if (!reportPeriodId || !locationId) return;
    emissionService
      .getAll(
        reportPeriodId,
        page,
        perPage,
        orderBy,
        order,
        `&locationId=${locationId}`,
      )
      .then((response) => {
        setEmissionRows(response.rows);
        setTotalRecords(response.count);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (requiredReportPeriodId) {
      getEmissionsForLocation(requiredReportPeriodId, selectedLocationId, inputPage);
    }
  }, [inputPage, perPage, orderBy, order]);

  // Selecting a location — from the Autocomplete or by clicking its circle on the map — resets
  // to the first page and reloads the records table beneath.
  const selectLocation = (locationId: string) => {
    setSelectedLocationId(locationId);
    if (locationId !== selectedLocationId) setInputPage(0);
    if (requiredReportPeriodId) getEmissionsForLocation(requiredReportPeriodId, locationId, 0);
  };

  const mapPoints: IMapPoint[] = useMemo(() => {
    const points = mapEmissions ? mapEmissions[emissionType] : undefined;
    if (!points) return [];
    return points.map((point) => ({
      id: point.id,
      label: point.label,
      value: point.value,
      lat: point.lat,
      lng: point.lng,
      sublabel: point.postcode || point.country || undefined,
      approximate: point.precision === 'country',
    }));
  }, [mapEmissions, emissionType]);

  // The location history stack keys its series by location label; the map points carry the ids,
  // so resolve a clicked series label back to its location id through this map. Locations with no
  // resolvable coordinates are absent from the map, so their history bars stay non-drillable.
  const locationIdByLabel = useMemo(() => {
    const map = new Map<string, string>();
    mapPoints.forEach((point) => { if (point.id != null) map.set(String(point.label), String(point.id)); });
    return map;
  }, [mapPoints]);

  const mapCaption = useMemo(() => {
    if (!mapEmissions) return undefined;
    const label = screenLabelsContext.getLabel('location', { plural: true });
    const { mapped, total, unmappedLocations } = mapEmissions.coverage;

    // Genuinely unplaceable is the rarer, more serious case, so it wins the caption. Name the
    // offenders so they can be fixed, but cap the list — this is one line, not an error report.
    if (unmappedLocations.length > 0) {
      const NAME_LIMIT = 3;
      const named = unmappedLocations.slice(0, NAME_LIMIT).map((location) => location.label).join(', ');
      const remainder = unmappedLocations.length - NAME_LIMIT;
      const listed = remainder > 0 ? `${named} and ${remainder} more` : named;
      return `${mapped} of ${total} ${label} mapped: ${listed} could not be placed.`;
    }

    // The common case: everything is on the map, but some pins are country centroids rather
    // than real postcodes. Say so, because it changes how the map should be read.
    const approximate = mapPoints.filter((point) => point.approximate).length;
    if (approximate === 0) return undefined;
    return `${mapPoints.length - approximate} of ${mapPoints.length} ${label} placed by postcode, `
      + `${approximate} shown at the centre of their country.`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapEmissions, mapPoints]);

  // Refetch the map after a bulk address fix so newly-placed locations appear immediately.
  const refreshMapData = () => {
    if (!requiredReportPeriodId) return;
    analyticsService
      .getMapData(requiredReportPeriodId, `?data=location`)
      .then(setMapEmissions)
      .catch((err) => console.log(err));
    locationService
      .getAllLocations(true)
      .then(setAllLocations)
      .catch((err) => console.log(err));
  };

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <AnalyticsByLocationSkeleton />
          </div>
        </Fade>
      )}

      {!isLoading && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              {pieCountryEmissions && pieCountryEmissions[emissionType] && (
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphDonutChart
                    enableTopN
                    defaultTopN={5}
                    title="GHG Emission Breakdown by Country"
                    tooltip="The greenhouse gas emission breakdown per country for the selected report period"
                    dataset={pieCountryEmissions[emissionType]}
                    colors={colors}
                    periodLabel={periodLabel}
                    comparison={comparisonPeriod && comparisonPieCountryEmissions?.[emissionType]
                      ? { dataset: comparisonPieCountryEmissions[emissionType], label: comparisonPeriod.label }
                      : undefined}
                    onSelect={(item) => drill.openDrill({ dimension: 'country', id: item.id, label: item.label, value: item.value })}
                  />
                </Grid>
              )}

              {stackedLocationEmissions &&
                stackedLocationEmissions[emissionType] && (
                  <Grid
                    item
                    xl={8}
                    lg={8}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <GraphBarChartStacked
                      enableTopN
                      defaultTopN={10}
                      title={`GHG Emission History for ${screenLabelsContext.getLabel(
                        'location',
                        { plural: true, capitalize: 'first' },
                      )}`}
                      tooltip={`The greenhouse gas emission history for each month in the selected report period per ${screenLabelsContext.getLabel(
                        'location',
                        { plural: false },
                      )}`}
                      dataset={stackedLocationEmissions[emissionType]}
                      series={buildGenericSeriesStack(
                        stackedLocationEmissions[emissionType],
                        valueFormatter,
                      )}
                      colors={colors}
                      comparison={comparisonPeriod && comparisonStackedLocationEmissions?.[emissionType]
                        ? {
                          totals: stackedRowTotals(comparisonStackedLocationEmissions[emissionType]),
                          label: comparisonPeriod.label,
                          valueFormatter,
                        }
                        : undefined}
                      onSelect={(seriesKey) => {
                        const id = locationIdByLabel.get(seriesKey);
                        if (id) drill.openDrill({ dimension: 'location', id, label: seriesKey });
                      }}
                    />
                  </Grid>
                )}

              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Suspense fallback={<Skeleton variant='rectangular' height={MAP_HEIGHT + 96} sx={{ borderRadius: 1 }} />}>
                  <GraphMapChart
                    title={`GHG Emissions by ${screenLabelsContext.getLabel('location', {
                      plural: false,
                      capitalize: 'first',
                    })}`}
                    tooltip={`Each circle is a ${screenLabelsContext.getLabel('location', {
                      plural: false,
                    })}, sized by area in proportion to its greenhouse gas emissions for the selected report period. Click a circle to drill into it and filter the records below.`}
                    points={mapPoints}
                    chartHeight={MAP_HEIGHT}
                    caption={mapCaption}
                    captionAction={canEditLocations && allLocations.length > 0 ? (
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<EditLocationOutlined fontSize='small' />}
                        onClick={() => setUpdateLocationsOpen(true)}
                      >
                        <Typography variant='caption' sx={{ pl: 0.5 }}>Update information</Typography>
                      </Button>
                    ) : undefined}
                    emptyMessage={`No ${screenLabelsContext.getLabel('location', {
                      plural: true,
                    })} with emissions could be placed on the map for this report period.`}
                    onSelect={(point) => {
                      // Keep the existing behaviour of filtering the records table, and also open
                      // the richer drill panel for the clicked site.
                      selectLocation(point.id);
                      drill.openDrill({ dimension: 'location', id: point.id, label: point.label, value: point.value });
                    }}
                  />
                </Suspense>
              </Grid>

              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Grid container>
                  <Grid item lg={8} sm={12}>
                    <Typography variant="subtitle1" sx={{ flex: 1, mb: 1 }}>
                      <b>
                        Emission Records by{' '}
                        {screenLabelsContext.getLabel('location', {
                          plural: false,
                          capitalize: 'first',
                        })}
                      </b>
                    </Typography>
                  </Grid>
                  <Grid item lg={4} sm={12}>
                    {allLocations.length > 0 && (
                      <Autocomplete
                        id="location-filter-autocomplete"
                        options={[...allLocations].sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
                        getOptionLabel={(option) => option.friendlyName ?? ''}
                        value={allLocations.find((l) => l.id === selectedLocationId) ?? null}
                        onChange={(_, value) => selectLocation(value?.id ?? '')}
                        size="small"
                        sx={{ width: '100%', mb: 2 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Filter"
                            variant="outlined"
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
                <EmissionsTable
                  emissions={emissionRows}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  setOrderBy={setOrderBy}
                  setOrder={setOrder}
                  backLink={'/analytics/dashboard?tab=emissions&dim=location'}
                  backText="Back to analytics"
                />
              </Grid>
            </Grid>
          </div>
        </Fade>
      )}

      <DrillDownDrawer
        open={drill.open}
        onClose={drill.close}
        selection={drill.selection}
        reportPeriodId={requiredReportPeriodId || ''}
        periodLabel={periodLabel}
        scopeTwoMethod={scopeTwoMethod}
        comparisonReportPeriodId={comparisonPeriod?.id}
        dimensionLabel={dimLabel(drill.selection?.dimension)}
      />

      <UpdateUnmappedLocationsDialog
        open={updateLocationsOpen}
        locations={allLocations}
        closeHandler={() => setUpdateLocationsOpen(false)}
        onUpdated={refreshMapData}
      />
    </>
  );
};
